/**
 * Scene helper overlays: reference grid and world axis indicator.
 *
 * Supports both the default UI-controlled grid and code-defined grids from
 * scene_grid() calls in OpenSCAD source. Code grids suppress the default grid;
 * the grid toggle controls visibility of all grids.
 */

import { useMemo } from 'react';
import { Grid, Text } from '@react-three/drei';
import * as THREE from 'three';
import type { SceneGrid } from '@/worker/types';

// ---------------------------------------------------------------------------
// WorldAxis — minimalistic X/Y/Z lines with labels
// ---------------------------------------------------------------------------

function WorldAxis({ size = 50 }: { size?: number }) {
  const lines = useMemo(() => {
    const material = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.4,
    });

    const xPoints = [new THREE.Vector3(0, 0, 0), new THREE.Vector3(size, 0, 0)];
    const xGeometry = new THREE.BufferGeometry().setFromPoints(xPoints);
    const xLine = new THREE.Line(xGeometry, material);

    const yPoints = [new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, size, 0)];
    const yGeometry = new THREE.BufferGeometry().setFromPoints(yPoints);
    const yLine = new THREE.Line(yGeometry, material);

    const zPoints = [new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, size)];
    const zGeometry = new THREE.BufferGeometry().setFromPoints(zPoints);
    const zLine = new THREE.Line(zGeometry, material);

    const group = new THREE.Group();
    group.add(xLine, yLine, zLine);
    return group;
  }, [size]);

  const labelOffset = size + 3;
  const labelProps = {
    fontSize: 4,
    color: 'white',
    fillOpacity: 0.4,
    anchorX: 'center' as const,
    anchorY: 'middle' as const,
  };

  return (
    <group>
      <primitive object={lines} />
      <Text position={[labelOffset, 0, 0]} {...labelProps}>
        x
      </Text>
      <Text position={[0, labelOffset, 0]} {...labelProps}>
        y
      </Text>
      <Text position={[0, 0, labelOffset]} {...labelProps}>
        z
      </Text>
    </group>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert an RGB 0-1 triple to a CSS hex color string. */
function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (v: number) =>
    Math.round(Math.max(0, Math.min(1, v)) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Slightly brighten an RGB triple for section lines vs cell lines. */
function brighten(
  rgb: [number, number, number],
  factor = 1.4,
): [number, number, number] {
  return [
    Math.min(1, rgb[0] * factor),
    Math.min(1, rgb[1] * factor),
    Math.min(1, rgb[2] * factor),
  ];
}

// ---------------------------------------------------------------------------
// CodeGrid — renders one scene_grid() entry
// ---------------------------------------------------------------------------

function CodeGrid({ grid }: { grid: SceneGrid }) {
  const position = grid.position;
  // Convert OpenSCAD degrees to Three.js radians (euler XYZ order)
  const rotationRad: [number, number, number] = [
    (grid.rotation[0] * Math.PI) / 180,
    (grid.rotation[1] * Math.PI) / 180,
    (grid.rotation[2] * Math.PI) / 180,
  ];

  const mainColor = rgbToHex(...grid.color);
  const cellColor = grid.cellColor ? rgbToHex(...grid.cellColor) : mainColor;
  const sectionColor = grid.sectionColor
    ? rgbToHex(...grid.sectionColor)
    : rgbToHex(...brighten(grid.color));

  const transparent = grid.opacity < 1;

  if (grid.type === 0) {
    // ── Plane: flat grid lines using drei <Grid> ──────────────────────────
    // Always use fadeStrength=1 to avoid pow(0,0) undefined behavior in GLSL.
    // For finite grids, set fadeDistance to half the grid size so the grid
    // fades to transparent at the mesh boundary.
    const fadeDistance = grid.infinite ? grid.fadeDistance : grid.size * 0.5;
    return (
      <Grid
        position={position}
        rotation={rotationRad}
        args={[grid.size, grid.size]}
        cellSize={grid.cellSize}
        cellThickness={0.5}
        cellColor={cellColor}
        sectionSize={grid.sectionSize}
        sectionThickness={1}
        sectionColor={sectionColor}
        fadeDistance={fadeDistance}
        fadeStrength={1}
        followCamera={false}
        infiniteGrid={grid.infinite}
      />
    );
  }

  if (grid.type === 1) {
    // ── Cube wireframe reference object ───────────────────────────────────
    return (
      <mesh position={position} rotation={rotationRad}>
        <boxGeometry args={[grid.size, grid.size, grid.size]} />
        <meshBasicMaterial
          color={mainColor}
          wireframe
          transparent={transparent}
          opacity={grid.opacity}
          depthWrite={!transparent}
        />
      </mesh>
    );
  }

  if (grid.type === 2) {
    // ── Sphere wireframe reference object ─────────────────────────────────
    return (
      <mesh position={position} rotation={rotationRad}>
        <sphereGeometry args={[grid.size / 2, 32, 16]} />
        <meshBasicMaterial
          color={mainColor}
          wireframe
          transparent={transparent}
          opacity={grid.opacity}
          depthWrite={!transparent}
        />
      </mesh>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// SceneHelpers — public component
// ---------------------------------------------------------------------------

export function SceneHelpers({
  showGrid,
  showAxis,
  codeGrids,
}: {
  showGrid: boolean;
  showAxis: boolean;
  /** Code-defined grids from scene_grid() calls. When present the default grid
   *  is suppressed (showGrid = false is passed from ThreeScene). */
  codeGrids?: SceneGrid[];
}) {
  return (
    <>
      {/* Default grid — shown only when no code grids are active */}
      {showGrid && (
        <Grid
          args={[100, 100]}
          cellSize={1}
          cellThickness={0.5}
          cellColor="#6e6e6e"
          sectionSize={10}
          sectionThickness={1}
          sectionColor="#9d9d9d"
          fadeDistance={200}
          fadeStrength={1}
          followCamera={false}
          infiniteGrid={true}
        />
      )}

      {/* Code-defined grids from scene_grid() calls */}
      {codeGrids?.map((grid, i) => (
        <CodeGrid key={i} grid={grid} />
      ))}

      {showAxis && <WorldAxis />}
    </>
  );
}
